import AutoNumeric from 'autonumeric';
import { initMoneyInputs } from './moneyInputs.js';
import { renderMonthlyBarCharts } from './monthlyBarChart.js';
import { initPagedSlider } from './pagedSlider.js';
import {
    trackCalculationCompleted,
    trackCalculatorStarted,
} from './analyticsTracker.js';

/**
 * Attaches an event listener to an element that, when triggered,
 * displays a target element.
 *
 * @param {string} actionElementId - The ID of the element to attach the event.
 * @param {string} action - The event type (e.g., "click").
 * @param {string} showElementId - The ID of the element to display.
 */
export function showHidedElement(actionElementId, action, showElementId) {
    const actionElement = document.getElementById(actionElementId);
    if (!actionElement) return;

    actionElement.addEventListener(action, (e) => {
        const container = document.getElementById(showElementId);
        if (!container) return;
        // Prevent form submission if container is not already visible.
        if (container.style.display === 'none' || container.style.display === '') {
            e.preventDefault();
        }
        container.style.display = 'block';
    });
}

/**
 * Initializes AutoNumeric formatting on a given input element.
 *
 * @param {HTMLElement} elem - The input element to format.
 */
function liveFormatNumberInputs(elem) {
    new AutoNumeric(elem, {
        digitGroupSeparator: '.',
        decimalCharacter:    ',',
        decimalPlaces:       2,
        minimumValue:        '0',
    });
}

/**
 * Populates the interest rate and amortization dropdowns with numeric options.
 * Default values are read from custom data attributes:
 * - "data-default-interest" for interest rate
 * - "data-default-amortization" for amortization
 */
function initDropdowns() {
    /**
     * Populates a select element with options from "start" to "end" in increments of "step".
     *
     * @param {string} selectId - The ID of the select element.
     * @param {number} start - The starting value.
     * @param {number} end - The ending value.
     * @param {number} step - The increment step.
     * @param {number} defaultValue - The value to be selected by default.
     * @param {string} unit - An optional unit suffix (e.g., '%').
     */
    const populateSelect = (selectId, start, end, step, defaultValue, unit = '') => {
        const selectElem = document.getElementById(selectId);
        if (!selectElem) return;
        // Clear any existing options
        selectElem.innerHTML = '';

        const normalizedDefault = Number(defaultValue.toFixed(2));

        for (let value = start; value <= end; value += step) {
            const option       = document.createElement('option');
            const numValue     = Number(value.toFixed(2));
            const formatted    = numValue.toFixed(2).replace('.', ',');
            option.value       = numValue;
            option.textContent = formatted + unit;

            if (normalizedDefault == numValue) {
                option.setAttribute('selected', true);
            }
            selectElem.appendChild(option);
        }
        // Set the default (selected) value if provided
        if (defaultValue) {
            selectElem.value = defaultValue;
        }
    };

    const interestRateSelect = document.getElementById('interestRate');
    const amortizationSelect = document.getElementById('amortization');
    if (!interestRateSelect || !amortizationSelect) return;

    if (interestRateSelect.options.length > 0 && amortizationSelect.options.length > 0) {
        return;
    }

    // Read default values from custom data attributes
    const defaultInterestRate = Number(interestRateSelect.getAttribute('data-default-interest'));
    const defaultAmortization = Number(amortizationSelect.getAttribute('data-default-amortization'));

    populateSelect('interestRate', 0, 10, 0.1, defaultInterestRate, '%');
    populateSelect('amortization', 0, 10, 0.1, defaultAmortization, '%');
}

/**
 * Initializes AutoNumeric formatting on all text inputs within specified forms.
 */
function initAutoNumeric() {
    // Format inputs in the quick-check form.
    const quickcheckElements = document.querySelectorAll('#quickcheck__form input[type="text"]');
    quickcheckElements.forEach(elem => liveFormatNumberInputs(elem));

    // Format inputs in the cashflow form.
    const cashflowElements = document.querySelectorAll('.cashflow__form input[type="text"]');
    cashflowElements.forEach(elem => liveFormatNumberInputs(elem));

    // Format inputs in the scenario boxes.
    const scenarioJumpElements = document.querySelectorAll('.scenario-grid .form-control[type="text"]');
    scenarioJumpElements.forEach(elem => liveFormatNumberInputs(elem));
}

/**
 * Track the first interaction with a server-side calculator form as a calculator start.
 * Submitting without any prior input also counts as a start.
 */
function initFormStartTracking(selector, calculatorKey) {
    const form = document.querySelector(selector);
    if (!form) return;

    let started = false;
    const trackStart = () => {
        if (started) return;
        started = true;
        trackCalculatorStarted(calculatorKey);
    };

    form.addEventListener('input', trackStart);
    form.addEventListener('change', trackStart);
    form.addEventListener('submit', trackStart);
}

function initDepreciationTracking() {
    initFormStartTracking('.depreciation__form', 'depreciation');
}

function initStandaloneCalculatorTracking() {
    initFormStartTracking('#equity-calculator .simple-calculator__form', 'equity');
    initFormStartTracking('#rental-yield-calculator .simple-calculator__form', 'rental_yield');
    initFormStartTracking('#transfer-tax-calculator .simple-calculator__form', 'transfer_tax');
}

function initCashflowAndQuickCheckTracking() {
    initFormStartTracking('.cashflow__form', 'cashflow');
    initFormStartTracking('#quickcheck__form', 'quickcheck');
}

function setupQuickCheckUsageToggle() {
    const quickcheckForm = document.getElementById('quickcheck__form');
    if (!quickcheckForm) return;

    const rentGroup = quickcheckForm.querySelector('[data-quickcheck-rent-group]');
    const rentInput = quickcheckForm.querySelector('[data-quickcheck-rent-input]');
    const usageInputs = quickcheckForm.querySelectorAll('input[name="usageType"]');
    if (!rentGroup || !rentInput || usageInputs.length === 0) return;

    const updateRentFieldVisibility = () => {
        const selectedUsageType = quickcheckForm.querySelector('input[name="usageType"]:checked')?.value ?? 'capitalInvestment';
        const isOwnerOccupied = selectedUsageType === 'ownerOccupied';

        rentGroup.classList.toggle('quick-check__form-group--hidden', isOwnerOccupied);
        rentInput.disabled = isOwnerOccupied;
        rentInput.required = !isOwnerOccupied;
        document.querySelectorAll('[data-capital-investment-only]').forEach((element) => {
            element.classList.toggle('d-none', isOwnerOccupied);
        });
        document.querySelectorAll('[data-owner-occupied-only]').forEach((element) => {
            element.classList.toggle('d-none', !isOwnerOccupied);
        });
    };

    usageInputs.forEach((input) => {
        input.addEventListener('change', updateRentFieldVisibility);
    });

    updateRentFieldVisibility();
}

/**
 * Sets up event listeners for the toggle buttons controlling
 * the detailed calculation container.
 */
function setupToggleButtons() {
    const updateDetailVisibility = (endpoint) => {
        fetch(endpoint, {
            method:  'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body:    JSON.stringify({})
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    window.location.reload();
                }
            })
            .catch(error => console.error('Error:', error));
    };

    // Show detailed calculation container when toggle button is clicked.
    const toggleBtn = document.getElementById('detailedCalculationToggle');
    if (toggleBtn) {
        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            updateDetailVisibility('/set-detail-visible');
        });
    }

    // Hide detailed calculation container when remove icon is clicked.
    const removeIcon = document.querySelector('#detailedCalculationContainer .icon-remove');
    if (removeIcon) {
        removeIcon.addEventListener('click', (e) => {
            e.preventDefault();
            updateDetailVisibility('/reset-calculation');
        });
    }

    const resetCashflowButton = document.getElementById('cashflow__reset');
    if (resetCashflowButton) {
        resetCashflowButton.addEventListener('click', (e) => {
            e.preventDefault();
            updateDetailVisibility('/reset-calculation');
        });
    }
}

/**
 * prevents redirect on scenario jump changes
 */
function blockRedirectOnScenario() {
    const scenarioGrids = document.querySelectorAll('.scenario-grid');
    if (scenarioGrids.length === 0) return;

    if (document.body.dataset.scenarioHandlersBound === 'true') {
        return;
    }

    document.body.dataset.scenarioHandlersBound = 'true';

    const submitScenarioForm = (form, submitter = null) => {
        if (!form) return;

        const formData = new FormData(form);
        if (submitter instanceof HTMLButtonElement && submitter.name) {
            formData.set(submitter.name, submitter.value);
        }
        const data = Object.fromEntries(formData.entries());
        const scenarioName = data.scenarioName;
        const scrollPosition = window.scrollY;

        fetch('/scenario', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify(data)
        })
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                    console.error('Error in inputs:', data.inputs);
                    console.error('Error:', data);

                    return;
                }

                if (!data.success || !data.html || !scenarioName) {
                    return;
                }

                const parser = new DOMParser();
                const documentFragment = parser.parseFromString(data.html, 'text/html');
                const selector = `[data-scenario-grid="${scenarioName}"]`;
                const updatedScenario = documentFragment.querySelector(selector);
                const currentScenario = document.querySelector(selector);

                if (!updatedScenario || !currentScenario) {
                    return;
                }

                currentScenario.replaceWith(updatedScenario);
                renderMonthlyBarCharts(updatedScenario);
                if (scenarioName === 'equity') {
                    const equity = Number(normalizeScenarioNumber(formData.get('scenarioJump')));
                    const kfwForm = document.querySelector('[data-kfw-comparison-form][data-base-loan-amount]');
                    if (kfwForm) {
                        kfwForm.dataset.equityAmount = String(equity);
                        kfwForm.dispatchEvent(new Event('kfw-equity-updated'));
                    }
                    const lbankForm = document.querySelector('[data-lbank-form][data-base-loan-amount]');
                    if (lbankForm) {
                        lbankForm.dataset.equityAmount = String(equity);
                        lbankForm.dispatchEvent(new Event('lbank-equity-updated'));
                    }
                }
                updatedScenario
                    .querySelectorAll('.form-control[type="text"]')
                    .forEach(elem => liveFormatNumberInputs(elem));
                window.scrollTo(0, scrollPosition);
            })
            .catch(error => console.error('Error:', error));
    };

    const normalizeScenarioNumber = (value) => {
        return String(value || '').replace(/\./g, '').replace(',', '.');
    };

    const setCashflowFieldValue = (field, value) => {
        if (!field) {
            return;
        }

        const autoNumericElement = AutoNumeric.getAutoNumericElement(field);
        if (autoNumericElement) {
            autoNumericElement.set(Number(value) || 0);
            return;
        }

        field.value = String(value);
    };

    const applyScenarioValue = (button) => {
        const form = document.querySelector('.cashflow__form');
        if (!form) {
            return;
        }

        const scenarioName = button.dataset.scenarioName;
        const topic = normalizeScenarioNumber(button.dataset.topic);
        const targetMap = {
            totalPrice: 'purchasePrice',
            rent: 'netRent',
            interest: 'interestRate',
            amortization: 'amortization',
        };
        const targetName = targetMap[scenarioName];
        const targetField = targetName ? form.querySelector(`[name="${targetName}"]`) : null;

        if (!targetField) {
            return;
        }

        setCashflowFieldValue(targetField, topic);
        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
            return;
        }

        form.submit();
    };

    document.addEventListener('click', (e) => {
        const applyEquityButton = e.target instanceof Element ? e.target.closest('[data-apply-owner-equity]') : null;
        if (applyEquityButton instanceof HTMLButtonElement) {
            const form = document.querySelector('.cashflow__form');
            const financing = form?.querySelector('[name="financing"]');
            const purchasePrice = form?.querySelector('[name="purchasePrice"]');
            const baseLoan = Number(document.querySelector('[data-kfw-comparison-form]')?.dataset.baseLoanAmount);
            const equity = Number(applyEquityButton.dataset.equityAmount);
            const price = Number(normalizeScenarioNumber(purchasePrice?.value));
            if (form && financing && price > 0 && Number.isFinite(baseLoan)) {
                const ratio = Math.max(0, (baseLoan - equity) / price * 100);
                const value = String(Math.round(ratio * 100000000) / 100000000);
                financing.add(new Option(`${new Intl.NumberFormat(document.documentElement.lang || 'de', { maximumFractionDigits: 2 }).format(ratio)}%`, value));
                financing.value = value;
                const applied = document.createElement('input');
                applied.type = 'hidden';
                applied.name = 'applyEquity';
                applied.value = '1';
                form.append(applied);
                form.requestSubmit();
            }
            return;
        }

        const shiftButton = e.target instanceof Element ? e.target.closest('.scenario-shift') : null;
        if (shiftButton instanceof HTMLButtonElement) {
            e.preventDefault();
            e.stopPropagation();
            submitScenarioForm(shiftButton.form, shiftButton);

            return;
        }

        const applyButton = e.target instanceof Element ? e.target.closest('[data-scenario-apply]') : null;
        if (applyButton instanceof HTMLButtonElement) {
            e.preventDefault();
            e.stopPropagation();
            applyScenarioValue(applyButton);

            return;
        }

        const scenarioRow = e.target instanceof Element ? e.target.closest('.scenario-row.scenario-data') : null;
        if (!scenarioRow || !window.matchMedia('(hover: none)').matches) {
            return;
        }

        document.querySelectorAll('.scenario-row.is-action-visible').forEach((row) => {
            if (row !== scenarioRow) {
                row.classList.remove('is-action-visible');
            }
        });
        scenarioRow.classList.toggle('is-action-visible');
    }, true);

    document.addEventListener('submit', (e) => {
        const form = e.target instanceof HTMLFormElement ? e.target : e.target.closest('form');
        if (!form || !form.closest('.scenario-grid')) return;

        e.preventDefault();
        submitScenarioForm(form, e.submitter);
    }, true);
}

function getCurrencyFormatter(fractionDigits = 0) {
    const locale = (document.documentElement.lang || 'de').replace('_', '-');
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

function getPercentFormatter(fractionDigits = 2) {
    const locale = (document.documentElement.lang || 'de').replace('_', '-');
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

function initRentCalculator() {
    const form = document.querySelector('.rent-calculator');
    if (!form) return;

    const layout = form.closest('[data-rent-layout]');
    const inputRent = document.getElementById('rent_monthly');
    const inputYears = document.getElementById('rent_years');
    const inputIncrease = document.getElementById('rent_increase');
    const totalValue = document.getElementById('rent_total_value');
    const breakdownBody = document.getElementById('rent_breakdown_body');
    const breakdownToggle = document.getElementById('rent_toggle_breakdown');
    const breakdownContainer = document.getElementById('rent_breakdown');
    const overviewTotal = document.getElementById('rent_overview_total');
    const overviewLastRent = document.getElementById('rent_overview_last_rent');
    const overviewIncrease = document.getElementById('rent_overview_increase');
    const overviewBars = document.getElementById('rent_overview_bars');
    const overviewLastYear = document.getElementById('rent_overview_last_year');

    if (
        !layout ||
        !inputRent ||
        !inputYears ||
        !inputIncrease ||
        !totalValue ||
        !breakdownBody ||
        !breakdownToggle ||
        !breakdownContainer
    ) {
        return;
    }

    const formatter = getCurrencyFormatter();
    const rentNumeric = inputRent.matches('[data-rent-autonumeric]')
        ? (AutoNumeric.getAutoNumericElement(inputRent) || new AutoNumeric(inputRent, {
            digitGroupSeparator: '.',
            decimalCharacter: ',',
            decimalPlaces: 2,
            decimalPlacesShownOnBlur: 2,
        }))
        : null;
    let hasTrackedStart = false;
    let hasTrackedCompleted = false;

    const getRentValue = () => {
        if (rentNumeric) {
            return Math.max(0, Number(rentNumeric.getNumber()) || 0);
        }

        const normalized = String(inputRent.value || '').replace(/\./g, '').replace(',', '.');
        return Math.max(0, Number.parseFloat(normalized) || 0);
    };

    const withYears = (template, years) => String(template || '').replace(':years', String(years));

    const setText = (element, text) => {
        if (element) {
            element.textContent = text;
        }
    };

    const renderBreakdown = (rows) => {
        breakdownBody.innerHTML = '';
        rows.forEach((row) => {
            const tr = document.createElement('tr');
            const yearCell = document.createElement('td');
            yearCell.textContent = String(row.year);
            const yearlyCell = document.createElement('td');
            yearlyCell.className = 'text-end';
            yearlyCell.textContent = formatter.format(Math.round(row.yearly));
            const cumulativeCell = document.createElement('td');
            cumulativeCell.className = 'text-end';
            cumulativeCell.textContent = formatter.format(Math.round(row.cumulative));
            tr.appendChild(yearCell);
            tr.appendChild(yearlyCell);
            tr.appendChild(cumulativeCell);
            breakdownBody.appendChild(tr);
        });
    };

    const renderBars = (rows) => {
        if (!overviewBars) return;

        overviewBars.innerHTML = '';
        const maxYearly = rows.reduce((max, row) => Math.max(max, row.yearly), 0);
        rows.forEach((row, index) => {
            const bar = document.createElement('span');
            bar.className = index === rows.length - 1
                ? 'rent-overview__bar rent-overview__bar--last'
                : 'rent-overview__bar';
            bar.style.height = `${maxYearly > 0 ? (row.yearly / maxYearly) * 100 : 0}%`;
            overviewBars.appendChild(bar);
        });
        overviewBars.setAttribute('aria-label', withYears(breakdownContainer.dataset.chartLabel, rows.length));
    };

    const renderOverview = (monthlyRent, rows, total) => {
        const lastRow = rows[rows.length - 1];

        setText(overviewTotal, formatter.format(Math.round(total)));
        setText(overviewLastRent, formatter.format(Math.round(lastRow.yearly / 12)));
        setText(overviewIncrease, formatter.format(Math.round(total - (monthlyRent * 12 * rows.length))));
        setText(overviewLastYear, withYears(breakdownContainer.dataset.yearsLabel, rows.length));
        renderBars(rows);
        renderBreakdown(rows);
    };

    const calculate = () => {
        const monthlyRent = getRentValue();
        const years = Math.min(100, Math.max(1, Number.parseInt(inputYears.value, 10) || 1));
        const annualIncrease = Math.max(0, Number.parseFloat(inputIncrease.value) || 0);

        if (monthlyRent <= 0) {
            totalValue.textContent = '—';
            breakdownBody.innerHTML = '';
            layout.classList.remove('rent-calculator-layout--has-result');
            return;
        }

        let total = 0;
        let yearlyRentBase = monthlyRent;
        const rows = [];

        for (let year = 1; year <= years; year += 1) {
            const yearlyRent = yearlyRentBase * 12;
            total += yearlyRent;
            rows.push({year, yearly: yearlyRent, cumulative: total});
            yearlyRentBase *= (1 + (annualIncrease / 100));
        }

        totalValue.textContent = formatter.format(Math.round(total));
        if (!hasTrackedCompleted && monthlyRent > 0) {
            hasTrackedCompleted = true;
            trackCalculationCompleted('rent');
        }

        renderOverview(monthlyRent, rows, total);
        layout.classList.add('rent-calculator-layout--has-result');

        form.dispatchEvent(new CustomEvent('rentCalculator.updated', {detail: {total, breakdown: rows}}));
    };

    let debounceTimeout = null;
    const scheduleCalculation = () => {
        if (debounceTimeout) {
            window.clearTimeout(debounceTimeout);
        }
        debounceTimeout = window.setTimeout(calculate, 150);
    };

    [inputRent, inputYears, inputIncrease].forEach((input) => {
        input.addEventListener('input', scheduleCalculation);
        input.addEventListener('input', () => {
            if (!hasTrackedStart && getRentValue() > 0) {
                hasTrackedStart = true;
                trackCalculatorStarted('rent');
            }
        });
    });

    const setRentInvalid = (isInvalid) => {
        inputRent.classList.toggle('is-invalid', isInvalid);
        inputRent.setAttribute('aria-invalid', isInvalid ? 'true' : 'false');
    };

    inputRent.addEventListener('input', () => {
        if (inputRent.classList.contains('is-invalid') && getRentValue() > 0) {
            setRentInvalid(false);
        }
    });

    breakdownToggle.addEventListener('click', () => {
        const isOpen = layout.classList.contains('rent-calculator-layout--breakdown-open');
        if (!isOpen && getRentValue() <= 0) {
            setRentInvalid(true);
            inputRent.focus();
            return;
        }

        if (!isOpen) {
            calculate();
        }

        layout.classList.toggle('rent-calculator-layout--breakdown-open', !isOpen);
        breakdownToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        breakdownToggle.textContent = isOpen
            ? (breakdownToggle.dataset.showLabel || breakdownToggle.textContent)
            : (breakdownToggle.dataset.hideLabel || breakdownToggle.textContent);
    });

    calculate();
}

function initRentToPurchasePowerCalculator() {
    const calculator = document.querySelector('[data-rtp-calculator]');
    if (!calculator) return;

    const form = calculator.querySelector('#rtp-form');
    const results = calculator.querySelector('#rtp-results');
    const advice = calculator.querySelector('[data-rtp-advice]');
    const endpoint = calculator.dataset.calculateUrl;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    if (!form || !results || !endpoint || !csrfToken) return;

    const formatter = getCurrencyFormatter();
    const loadingText = calculator.dataset.loadingText || '...';
    const errorText = calculator.dataset.errorText || 'Error during calculation.';
    const numericInputs = Array.from(form.querySelectorAll('[data-rtp-autonumeric]'));
    const numericInstances = {};
    let hasTrackedStart = false;
    const setInputInvalid = (input, isInvalid) => {
        input.classList.toggle('is-invalid', isInvalid);
        input.setAttribute('aria-invalid', isInvalid ? 'true' : 'false');
    };

    numericInputs.forEach((input) => {
        const instance = AutoNumeric.getAutoNumericElement(input) || new AutoNumeric(input, {
            digitGroupSeparator: '.',
            decimalCharacter: ',',
            decimalPlaces: 2,
            decimalPlacesShownOnBlur: 2,
        });
        numericInstances[input.name] = instance;
        input.addEventListener('input', () => {
            if (input.classList.contains('is-invalid') && String(input.value || '').trim() !== '') {
                setInputInvalid(input, false);
            }
        });
        input.addEventListener('input', () => {
            if (hasTrackedStart) {
                return;
            }
            hasTrackedStart = true;
            trackCalculatorStarted('rent_to_purchase_power');
        });
    });

    const labels = {
        headline: calculator.dataset.resultHeadline || '',
        monthlyBudget: calculator.dataset.resultMonthlyBudget || '',
        loan: calculator.dataset.resultLoan || '',
        equity: calculator.dataset.resultEquity || '',
        maxBudget: calculator.dataset.resultMaxBudget || '',
        purchasePrice: calculator.dataset.resultPurchasePrice || '',
        acquisitionCosts: calculator.dataset.resultAcquisitionCosts || '',
        note: calculator.dataset.resultNote || '',
        breakdownFinancing: calculator.dataset.breakdownFinancing || '',
        breakdownUsage: calculator.dataset.breakdownUsage || '',
        infoMonthlyBudget: calculator.dataset.infoMonthlyBudget || '',
        infoLoan: calculator.dataset.infoLoan || '',
        infoEquity: calculator.dataset.infoEquity || '',
        infoMaxBudget: calculator.dataset.infoMaxBudget || '',
        infoPurchasePrice: calculator.dataset.infoPurchasePrice || '',
        infoAcquisitionCosts: calculator.dataset.infoAcquisitionCosts || '',
    };

    const toMoney = (value) => formatter.format(Math.round(Number(value) || 0));
    const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const getInputValue = (fieldName) => {
        const numericInstance = numericInstances[fieldName];
        if (numericInstance) {
            return Math.max(0, Number(numericInstance.getNumber()) || 0);
        }

        const input = form[fieldName];
        if (!input) {
            return 0;
        }

        const normalized = String(input.value || '').replace(/\./g, '').replace(',', '.');
        return Math.max(0, Number.parseFloat(normalized) || 0);
    };
    const renderResultLabel = (label, tooltip) => `
        <span class="calcimo-rtp-result__label">
            <span>${escapeHtml(label)}</span>
            <button
                type="button"
                class="cashflow__info-button"
                data-toggle="tooltip"
                title="${escapeHtml(tooltip)}"
                aria-label="${escapeHtml(tooltip)}"
                tabindex="-1"
            >
                <i class="fas fa-info-circle"></i>
            </button>
        </span>
    `;
    const toShare = (value, total) => (total > 0 ? Math.max(0, Math.min(100, (Number(value) || 0) / total * 100)) : 0);
    const renderBreakdown = (title, total, firstPart, secondPart) => `
        <div class="equity-breakdown__group rtp-result__group">
            <h4 class="h5">${escapeHtml(title)}</h4>
            <div class="equity-breakdown__track" aria-hidden="true">
                <span class="equity-breakdown__segment equity-breakdown__segment--${firstPart.tone}" style="width: ${toShare(firstPart.value, total)}%"></span>
                <span class="equity-breakdown__segment equity-breakdown__segment--${secondPart.tone}" style="width: ${toShare(secondPart.value, total)}%"></span>
            </div>
            <dl class="equity-breakdown__legend">
                ${[firstPart, secondPart].map((part) => `
                    <div class="equity-breakdown__item equity-breakdown__item--${part.tone}">
                        <dt>${renderResultLabel(part.label, part.info)}</dt>
                        <dd>${toMoney(part.value)}</dd>
                    </div>
                `).join('')}
            </dl>
        </div>
    `;
    const showResult = (html) => {
        results.innerHTML = html;
        calculator.classList.add('rtp-calculator--has-result');
    };

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const emptyInputs = numericInputs.filter((input) => String(input.value || '').trim() === '');
        numericInputs.forEach((input) => setInputInvalid(input, emptyInputs.includes(input)));
        if (emptyInputs.length > 0) {
            emptyInputs[0].focus();
            return;
        }

        showResult(`
            <div class="card w-100 mb-global rtp-result rtp-result--loading">
                <div class="card-body">${escapeHtml(loadingText)}</div>
            </div>
        `);

        const payload = {
            rent: getInputValue('rent'),
            savings: getInputValue('savings'),
            equity: getInputValue('equity'),
        };

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok || data.error) {
                const firstValidationError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstValidationError || data.error || errorText);
            }
            trackCalculationCompleted('rent_to_purchase_power');

            const maxBudget = Number(data.max_budget) || 0;
            showResult(`
                <div class="card w-100 mb-global rtp-result">
                    <div class="card-body d-flex flex-column">
                        <h3 class="h4">${escapeHtml(labels.headline)}</h3>
                        <div class="rtp-result__hero">
                            <span class="rtp-result__hero-label">${renderResultLabel(labels.purchasePrice, labels.infoPurchasePrice)}</span>
                            <span class="rtp-result__hero-value">${toMoney(data.purchase_price)}</span>
                        </div>
                        <dl class="rtp-result__stats">
                            <div class="rtp-result__stat">
                                <dt>${renderResultLabel(labels.monthlyBudget, labels.infoMonthlyBudget)}</dt>
                                <dd>${toMoney(data.monthly_budget)}</dd>
                            </div>
                            <div class="rtp-result__stat">
                                <dt>${renderResultLabel(labels.maxBudget, labels.infoMaxBudget)}</dt>
                                <dd>${toMoney(maxBudget)}</dd>
                            </div>
                        </dl>
                        ${renderBreakdown(labels.breakdownFinancing, maxBudget,
                            { tone: 'equity', label: labels.equity, info: labels.infoEquity, value: data.equity },
                            { tone: 'loan', label: labels.loan, info: labels.infoLoan, value: data.loan_amount })}
                        ${renderBreakdown(labels.breakdownUsage, maxBudget,
                            { tone: 'purchase', label: labels.purchasePrice, info: labels.infoPurchasePrice, value: data.purchase_price },
                            { tone: 'costs', label: labels.acquisitionCosts, info: labels.infoAcquisitionCosts, value: data.acquisition_costs })}
                        <p class="small text-muted mt-auto pt-3 mb-0">${escapeHtml(labels.note)}</p>
                    </div>
                </div>
            `);
            if (advice) {
                advice.hidden = false;
            }
            if (window.matchMedia('(max-width: 991.98px)').matches) {
                const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                results.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
            }

            if (window.jQuery && window.jQuery.fn.tooltip) {
                window.jQuery(results).find('[data-toggle="tooltip"]').tooltip();
            }
        } catch (error) {
            showResult(`<div class="alert alert-danger w-100 mb-global">${escapeHtml(error.message || errorText)}</div>`);
        }
    });
}

function initTaxCalculator() {
    const calculator = document.querySelector('[data-tax-calculator]');
    if (!calculator) return;

    const form = calculator.querySelector('#tax-form');
    const results = calculator.querySelector('#tax-results');
    const deductionCard = document.querySelector('[data-tax-deduction-card]');
    const deductionForm = deductionCard?.querySelector('[data-tax-deduction-form]');
    const deductionInput = deductionCard?.querySelector('[data-tax-deductible-autonumeric]');
    const deductionSubmitButton = deductionCard?.querySelector('[data-tax-deduction-submit]');
    const deductionErrorNode = deductionCard?.querySelector('[data-tax-deduction-error]');
    const deductionComparison = deductionCard?.querySelector('[data-tax-deduction-comparison]');
    const endpoint = calculator.dataset.calculateUrl;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const taxStateStorageKey = `calcimo-tax-calculator-state:${window.location.pathname}`;
    let hasTrackedStart = false;

    if (!form || !results || !endpoint || !csrfToken) return;

    const currencyFormatter = getCurrencyFormatter(2);
    const percentFormatter = getPercentFormatter(2);
    const loadingText = calculator.dataset.loadingText || '...';
    const errorText = calculator.dataset.errorText || 'Error during calculation.';
    const churchTaxLiabilityInput = form.querySelector('#tax_church_tax_liability');
    const churchTaxStateGroup = form.querySelector('#tax_church_state_group');
    const churchTaxStateInput = form.querySelector('#tax_church_tax_state');
    let taxComparisonChart = null;

    const labels = {
        validationIncomeRequired: calculator.dataset.validationIncomeRequired || '',
        validationDeductionRequired: calculator.dataset.validationDeductionRequired || '',
        summaryTemplate: calculator.dataset.summaryTemplate || '',
        summaryMarried: calculator.dataset.summaryMarried || '',
        summarySingle: calculator.dataset.summarySingle || '',
        summaryChildrenNone: calculator.dataset.summaryChildrenNone || '',
        summaryChildrenSingle: calculator.dataset.summaryChildrenSingle || '',
        summaryChildrenMultiple: calculator.dataset.summaryChildrenMultiple || '',
        summaryChurchNone: calculator.dataset.summaryChurchNone || '',
        summaryChurchTemplate: calculator.dataset.summaryChurchTemplate || '',
        summaryTaxesIntro: calculator.dataset.summaryTaxesIntro || '',
        tableTitle: calculator.dataset.tableTitle || '',
        tableIncomeTax: calculator.dataset.tableIncomeTax || '',
        tableSolidarity: calculator.dataset.tableSolidarity || '',
        tableChurch: calculator.dataset.tableChurch || '',
        tableTotal: calculator.dataset.tableTotal || '',
        averageTemplate: calculator.dataset.averageTemplate || '',
        marginalTitle: calculator.dataset.marginalTitle || '',
        marginalTemplate: calculator.dataset.marginalTemplate || '',
        soliInfoTitle: calculator.dataset.soliInfoTitle || '',
        soliInfoText: calculator.dataset.soliInfoText || '',
        stateBadenWuerttemberg: calculator.dataset.stateBw || '',
        stateBayern: calculator.dataset.stateBayern || '',
        stateOther: calculator.dataset.stateOther || '',
        deductionBeforeTitle: calculator.dataset.deductionBeforeTitle || '',
        deductionAfterTitle: calculator.dataset.deductionAfterTitle || '',
        deductionTaxSaving: calculator.dataset.deductionTaxSaving || '',
        chartCurrentLabel: calculator.dataset.chartCurrentLabel || '',
        chartAfterLabel: calculator.dataset.chartAfterLabel || '',
    };

    const taxableIncomeInput = form.querySelector('[name="taxable_income"]');
    const taxableIncomeNumeric = taxableIncomeInput && taxableIncomeInput.matches('[data-tax-autonumeric]')
        ? (AutoNumeric.getAutoNumericElement(taxableIncomeInput) || new AutoNumeric(taxableIncomeInput, {
            digitGroupSeparator: '.',
            decimalCharacter: ',',
            decimalPlaces: 2,
            decimalPlacesShownOnBlur: 2,
        }))
        : null;

    const toMoney = (value) => currencyFormatter.format(Number(value) || 0);
    const toPercent = (value) => `${percentFormatter.format(Number(value) || 0)} %`;
    const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const renderFormError = (message) => `<div class="invalid-feedback d-block mt-3">${escapeHtml(message || errorText)}</div>`;

    const replacePlaceholders = (template, placeholders) => {
        return Object.entries(placeholders).reduce(
            (text, [key, value]) => text.replace(new RegExp(`:${key}`, 'g'), String(value)),
            template
        );
    };
    const renderInfoLabel = (label, tooltip) => `
        <span class="calcimo-rtp-result__label">
            <span>${escapeHtml(label)}</span>
            <button
                type="button"
                class="cashflow__info-button"
                data-toggle="tooltip"
                title="${escapeHtml(tooltip)}"
                aria-label="${escapeHtml(tooltip)}"
                tabindex="-1"
            >
                <i class="fas fa-info-circle"></i>
            </button>
        </span>
    `;
    const resolveChurchTaxStateLabel = (state) => {
        if (state === 'baden-wuerttemberg') return labels.stateBadenWuerttemberg;
        if (state === 'bayern') return labels.stateBayern;
        return labels.stateOther;
    };
    const setChurchStateVisibility = () => {
        if (!churchTaxLiabilityInput || !churchTaxStateGroup || !churchTaxStateInput) {
            return;
        }

        const hasLiability = Number(churchTaxLiabilityInput.value) === 1;
        churchTaxStateGroup.hidden = !hasLiability;
        churchTaxStateInput.disabled = !hasLiability;
    };
    setChurchStateVisibility();
    churchTaxLiabilityInput?.addEventListener('change', setChurchStateVisibility);
    form.querySelectorAll('input, select').forEach((inputElement) => {
        inputElement.addEventListener('input', () => {
            if (hasTrackedStart) {
                return;
            }
            hasTrackedStart = true;
            trackCalculatorStarted('tax');
        });
        inputElement.addEventListener('change', () => {
            if (hasTrackedStart) {
                return;
            }
            hasTrackedStart = true;
            trackCalculatorStarted('tax');
        });
    });
    const deductibleNumeric = deductionInput
        ? new AutoNumeric(deductionInput, {
            digitGroupSeparator: '.',
            decimalCharacter: ',',
            decimalPlaces: 2,
            decimalPlacesShownOnBlur: 2,
        })
        : null;
    const getDeductionNumericString = () => {
        if (deductibleNumeric) {
            return String(deductibleNumeric.getNumericString() || '').trim();
        }

        return String(deductionInput?.value || '').trim();
    };
    const getDeductionValue = () => {
        if (deductibleNumeric) {
            return Math.max(0, Number(deductibleNumeric.getNumber()) || 0);
        }

        return Math.max(0, Number.parseFloat(String(deductionInput?.value || '').replace(/\./g, '').replace(',', '.')) || 0);
    };
    const updateDeductionSubmitState = () => {
        const hasValue = getDeductionNumericString() !== '';
        if (deductionSubmitButton) {
            deductionSubmitButton.disabled = !hasValue;
        }
    };
    const setDeductionInputValue = (value) => {
        const numericValue = Math.max(0, Number(value) || 0);
        if (deductibleNumeric) {
            if (numericValue > 0) {
                deductibleNumeric.set(numericValue);
            } else {
                deductibleNumeric.clear();
            }
        } else if (deductionInput) {
            deductionInput.value = numericValue > 0 ? String(numericValue) : '';
        }
        updateDeductionSubmitState();
    };
    const saveTaxState = (state) => {
        try {
            window.sessionStorage.setItem(taxStateStorageKey, JSON.stringify(state));
        } catch (error) {
            // Ignore storage errors so calculator behavior is unaffected.
        }
    };
    const loadTaxState = () => {
        try {
            const rawState = window.sessionStorage.getItem(taxStateStorageKey);
            if (!rawState) {
                return null;
            }
            return JSON.parse(rawState);
        } catch (error) {
            return null;
        }
    };
    updateDeductionSubmitState();
    deductionInput?.addEventListener('input', updateDeductionSubmitState);
    deductionInput?.addEventListener('blur', updateDeductionSubmitState);

    const getInputValue = (fieldName) => {
        if (fieldName === 'taxable_income' && taxableIncomeNumeric) {
            return Math.max(0, Number(taxableIncomeNumeric.getNumber()) || 0);
        }

        const input = form[fieldName];
        if (!input) {
            return 0;
        }

        if (input.type === 'number' || input.tagName.toLowerCase() === 'select') {
            return Number(input.value) || 0;
        }

        const normalized = String(input.value || '').replace(/\./g, '').replace(',', '.');
        return Number.parseFloat(normalized) || 0;
    };
    const renderTaxBlock = (scenario, title) => {
        const averageText = replacePlaceholders(labels.averageTemplate, {
            averageTaxRate: toPercent(scenario.average_tax_rate),
        });
        const marginalText = replacePlaceholders(labels.marginalTemplate, {
            taxableIncome: toMoney(scenario.taxable_income),
            marginalBaseAmount: toMoney(scenario.marginal_base_amount),
            marginalTaxAmount: toMoney(scenario.marginal_tax_amount),
            marginalTaxRate: toPercent(scenario.marginal_tax_rate),
        });

        return `
            <div class="calcimo-tax-result__scenario">
                <h5 class="calcimo-tax-result__subheadline">${escapeHtml(title)}</h5>
                <dl class="calcimo-tax-result__list">
                    <div class="calcimo-tax-result__item">
                        <dt>${escapeHtml(labels.tableIncomeTax)}</dt>
                        <dd>${toMoney(scenario.income_tax)}</dd>
                    </div>
                    <div class="calcimo-tax-result__item">
                        <dt>${renderInfoLabel(labels.tableSolidarity, labels.soliInfoText)}</dt>
                        <dd>${toMoney(scenario.solidarity_surcharge)}</dd>
                    </div>
                    <div class="calcimo-tax-result__item">
                        <dt>${escapeHtml(labels.tableChurch)}</dt>
                        <dd>${toMoney(scenario.church_tax)}</dd>
                    </div>
                    <div class="calcimo-tax-result__item calcimo-tax-result__item--highlight">
                        <dt>${escapeHtml(labels.tableTotal)}</dt>
                        <dd>${toMoney(scenario.total_tax)}</dd>
                    </div>
                </dl>
                <p class="calcimo-tax-result__average">${escapeHtml(averageText)}</p>
                <p class="calcimo-tax-result__marginal">${escapeHtml(marginalText)}</p>
            </div>
        `;
    };
    const renderTaxComparisonChart = (beforeTotalTax, afterTotalTax, canvas) => {
        if (taxComparisonChart) {
            taxComparisonChart.destroy();
            taxComparisonChart = null;
        }

        if (!canvas || typeof window.Chart !== 'function') {
            return;
        }

        const rootStyles = getComputedStyle(document.documentElement);
        const beforeColor = rootStyles.getPropertyValue('--feel-bad').trim() || '#e14eca';
        const afterColor = rootStyles.getPropertyValue('--color-neutral').trim() || '#00f2c3';
        const hexToRgba = (hex, alpha) => {
            const normalizedHex = String(hex || '').trim();
            const r = parseInt(normalizedHex.slice(1, 3), 16);
            const g = parseInt(normalizedHex.slice(3, 5), 16);
            const b = parseInt(normalizedHex.slice(5, 7), 16);
            if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
                return `rgba(225, 78, 202, ${alpha})`;
            }
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const beforeGradient = ctx.createLinearGradient(0, 230, 0, 50);
        beforeGradient.addColorStop(1, hexToRgba(beforeColor, 0.2));
        beforeGradient.addColorStop(0.4, hexToRgba(beforeColor, 0.0));
        beforeGradient.addColorStop(0, hexToRgba(beforeColor, 0));

        const afterGradient = ctx.createLinearGradient(0, 230, 0, 50);
        afterGradient.addColorStop(1, hexToRgba(afterColor, 0.2));
        afterGradient.addColorStop(0.4, hexToRgba(afterColor, 0.0));
        afterGradient.addColorStop(0, hexToRgba(afterColor, 0));

        taxComparisonChart = new window.Chart(ctx, {
            type: 'bar',
            data: {
                labels: [labels.tableTitle],
                datasets: [
                    {
                        label: labels.chartCurrentLabel,
                        backgroundColor: beforeGradient,
                        hoverBackgroundColor: beforeGradient,
                        borderColor: beforeColor,
                        borderWidth: 2,
                        data: [beforeTotalTax],
                    },
                    {
                        label: labels.chartAfterLabel,
                        backgroundColor: afterGradient,
                        hoverBackgroundColor: afterGradient,
                        borderColor: afterColor,
                        borderWidth: 2,
                        data: [afterTotalTax],
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                legend: {
                    display: true,
                    position: 'bottom',
                },
                scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                        },
                    }],
                },
            },
        });
    };
    const submitTaxCalculation = async (
        deductibleAmount = 0,
        showDeductionImpact = false,
        trackCompletion = true
    ) => {
        results.innerHTML = `<div class="calcimo-tax-result calcimo-tax-result--loading text-center">${escapeHtml(loadingText)}</div>`;

        const taxableIncomeRaw = taxableIncomeNumeric
            ? taxableIncomeNumeric.getNumericString()
            : String(taxableIncomeInput?.value || '').trim();
        if (String(taxableIncomeRaw || '').trim() === '') {
            results.innerHTML = renderFormError(labels.validationIncomeRequired || errorText);
            taxableIncomeInput?.focus();
            return;
        }

            const payload = {
            taxable_income: getInputValue('taxable_income'),
            is_married: Number(getInputValue('is_married')) === 1,
            children: Math.max(0, Number.parseInt(getInputValue('children'), 10) || 0),
            church_tax_liability: Number(getInputValue('church_tax_liability')) === 1,
            church_tax_state: String(form.church_tax_state?.value || 'other'),
            assessment_year: Number.parseInt(getInputValue('assessment_year'), 10) || 2026,
            deductible_amount: Math.max(0, deductibleAmount),
        };

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok || data.error) {
                const firstValidationError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstValidationError || data.error || errorText);
            }

            const maritalStatus = data.is_married ? labels.summaryMarried : labels.summarySingle;
            const childrenText = data.children === 0
                ? labels.summaryChildrenNone
                : data.children === 1
                    ? labels.summaryChildrenSingle
                    : replacePlaceholders(labels.summaryChildrenMultiple, {count: data.children});
            const churchStateLabel = resolveChurchTaxStateLabel(data.church_tax_state);
            const churchText = data.church_tax_liability
                ? replacePlaceholders(labels.summaryChurchTemplate, {
                    churchTaxRate: toPercent(data.church_tax_rate),
                    churchTaxState: churchStateLabel,
                })
                : labels.summaryChurchNone;

            const summaryText = replacePlaceholders(labels.summaryTemplate, {
                maritalStatus,
                children: childrenText,
                church: churchText,
                taxableIncome: toMoney(data.taxable_income),
                year: data.assessment_year,
            });
            const deductionAnalysis = data.deduction_analysis || {};
            const deductionBefore = deductionAnalysis.before || data;
            const deductionAfter = deductionAnalysis.after || data;
            const taxSaving = Number(deductionAnalysis.tax_saving || 0);
            const comparisonMarkup = showDeductionImpact ? `
                    <div class="calcimo-tax-deduction-card__comparison-grid">
                        <div class="calcimo-slider calcimo-tax-deduction-card__scenario-slider" data-tax-deduction-slider>
                            <div class="calcimo-slider__viewport" data-tax-deduction-viewport>
                                <div class="calcimo-slider__track calcimo-tax-deduction-card__scenarios" data-tax-deduction-track>
                                    <article class="calcimo-slider__slide">
                                        ${renderTaxBlock(deductionBefore, labels.deductionBeforeTitle)}
                                    </article>
                                    <article class="calcimo-slider__slide">
                                        ${renderTaxBlock(deductionAfter, labels.deductionAfterTitle)}
                                    </article>
                                </div>
                            </div>
                            <div class="calcimo-slider__controls" data-tax-deduction-controls>
                                <button type="button" class="btn btn-secondary btn-sm calcimo-slider__button" data-tax-deduction-prev aria-label="Previous">
                                    <i class="tim-icons icon-minimal-left"></i>
                                </button>
                                <div class="calcimo-slider__dots" data-tax-deduction-dots aria-label="Pages"></div>
                                <button type="button" class="btn btn-secondary btn-sm calcimo-slider__button" data-tax-deduction-next aria-label="Next">
                                    <i class="tim-icons icon-minimal-right"></i>
                                </button>
                            </div>
                        </div>
                        <div class="chart-area calcimo-tax-deduction-card__chart">
                            <canvas data-tax-comparison-chart></canvas>
                        </div>
                    </div>
                    <p class="calcimo-tax-deduction-card__saving"><strong>${escapeHtml(labels.deductionTaxSaving)}:</strong> ${toMoney(taxSaving)}</p>
            ` : '';

            results.innerHTML = `
                <div class="calcimo-tax-result">
                    <h4 class="calcimo-tax-result__headline">${escapeHtml(labels.tableTitle)}</h4>
                    <p class="calcimo-tax-result__summary">${escapeHtml(summaryText)} ${escapeHtml(labels.summaryTaxesIntro)}</p>
                    ${renderTaxBlock(data, labels.deductionBeforeTitle)}
                </div>
            `;
            if (deductionCard && deductionComparison) {
                deductionCard.hidden = false;
                deductionComparison.innerHTML = comparisonMarkup;
                deductionComparison.hidden = !showDeductionImpact;
            }

            if (showDeductionImpact) {
                const chartCanvas = deductionComparison?.querySelector('[data-tax-comparison-chart]');
                renderTaxComparisonChart(deductionBefore.total_tax, deductionAfter.total_tax, chartCanvas);
                const sliderRoot = deductionComparison?.querySelector('[data-tax-deduction-slider]');
                if (sliderRoot) {
                    initPagedSlider(sliderRoot, {
                        viewportSelector: '[data-tax-deduction-viewport]',
                        trackSelector: '[data-tax-deduction-track]',
                        controlsSelector: '[data-tax-deduction-controls]',
                        prevSelector: '[data-tax-deduction-prev]',
                        nextSelector: '[data-tax-deduction-next]',
                        dotsSelector: '[data-tax-deduction-dots]',
                        slideSelector: '.calcimo-slider__slide',
                        visibleSlides: () => 1,
                        hideControlsAtWidth: 676,
                    });
                }
            } else {
                renderTaxComparisonChart(0, 0, null);
            }
            if (trackCompletion) {
                trackCalculationCompleted('tax', {
                    ctaKey: showDeductionImpact ? 'tax_deduction_calculate' : null,
                });
            }
            saveTaxState({
                taxable_income: payload.taxable_income,
                is_married: payload.is_married,
                children: payload.children,
                church_tax_liability: payload.church_tax_liability,
                church_tax_state: payload.church_tax_state,
                assessment_year: payload.assessment_year,
                deductible_amount: payload.deductible_amount,
                show_deduction_impact: showDeductionImpact,
            });

            if (window.jQuery && window.jQuery.fn.tooltip) {
                window.jQuery(results).find('[data-toggle="tooltip"]').tooltip();
                if (deductionComparison) {
                    window.jQuery(deductionComparison).find('[data-toggle="tooltip"]').tooltip();
                }
            }
        } catch (error) {
            results.innerHTML = renderFormError(error.message || errorText);
        }
    };

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        await submitTaxCalculation(0, false);
    });
    deductionForm?.addEventListener('submit', async (deductionEvent) => {
        deductionEvent.preventDefault();
        const deductionRaw = getDeductionNumericString();
        if (deductionRaw === '') {
            if (deductionErrorNode) {
                deductionErrorNode.textContent = labels.validationDeductionRequired || labels.validationIncomeRequired || errorText;
                deductionErrorNode.hidden = false;
            }
            deductionInput?.focus();
            return;
        }

        if (deductionErrorNode) {
            deductionErrorNode.hidden = true;
            deductionErrorNode.textContent = '';
        }

        await submitTaxCalculation(getDeductionValue(), true);
    });
    const storedTaxState = loadTaxState();
    if (storedTaxState) {
        const restoredTaxableIncome = Math.max(0, Number(storedTaxState.taxable_income) || 0);
        if (taxableIncomeNumeric) {
            taxableIncomeNumeric.set(restoredTaxableIncome);
        } else if (taxableIncomeInput) {
            taxableIncomeInput.value = String(restoredTaxableIncome);
        }
        form.is_married.value = Number(storedTaxState.is_married) === 1 || storedTaxState.is_married === true ? '1' : '0';
        form.children.value = String(Math.max(0, Number.parseInt(storedTaxState.children, 10) || 0));
        form.church_tax_liability.value = Number(storedTaxState.church_tax_liability) === 1 || storedTaxState.church_tax_liability === true ? '1' : '0';
        if (form.church_tax_state) {
            form.church_tax_state.value = String(storedTaxState.church_tax_state || 'other');
        }
        form.assessment_year.value = String(Number.parseInt(storedTaxState.assessment_year, 10) || 2026);
        setChurchStateVisibility();
        setDeductionInputValue(storedTaxState.deductible_amount);

        if (restoredTaxableIncome > 0) {
            submitTaxCalculation(
                Math.max(0, Number(storedTaxState.deductible_amount) || 0),
                storedTaxState.show_deduction_impact === true,
                false
            );
        }
    }
}

/**
 * Main initialization function. Sets up event listeners and input formatting.
 * This function is safe to run on any page even if some elements are missing.
 */
function initApp() {
    initAutoNumeric();
    initMoneyInputs();
    initHouseFeeHelper();
    initDepreciationTracking();
    initStandaloneCalculatorTracking();
    initCashflowAndQuickCheckTracking();
    setupQuickCheckUsageToggle();
    setupToggleButtons();
    initDropdowns();
    blockRedirectOnScenario();
    initRentCalculator();
    initRentToPurchasePowerCalculator();
    initTaxCalculator();
    const portfolioSaveButton = document.querySelector('[data-portfolio-calculation-save]');
    const cashflowForm = document.querySelector('.cashflow__form');
    if (portfolioSaveButton && cashflowForm) {
        const markCalculationOutdated = () => {
            portfolioSaveButton.disabled = true;
        };
        cashflowForm.addEventListener('input', markCalculationOutdated);
        cashflowForm.addEventListener('change', markCalculationOutdated);
    }

    // Setup additional show/hide event listeners.
    showHidedElement('detailedCalculationToggle', 'click', 'detailedCalculationContainer');
}

function initHouseFeeHelper() {
    const helper = document.querySelector('[data-house-fee-helper]');
    if (!helper) return;

    const mode = helper.querySelector('[data-house-fee-mode]');
    const manual = helper.querySelector('[data-house-fee-manual]');
    const reserve = helper.querySelector('[data-house-fee-reserve]');
    const label = helper.querySelector('[data-house-fee-label]');
    const switchButton = helper.querySelector('[data-house-fee-switch]');

    switchButton.addEventListener('click', () => {
        const useReserve = mode.value !== 'reserve';
        mode.value = useReserve ? 'reserve' : 'houseFee';
        mode.dispatchEvent(new Event('change', { bubbles: true }));
        manual.hidden = useReserve;
        reserve.hidden = !useReserve;
        manual.querySelector('input').disabled = useReserve;
        reserve.querySelectorAll('input, select').forEach((field) => {
            field.disabled = !useReserve;
        });
        label.textContent = useReserve ? label.dataset.reserveLabel : label.dataset.manualLabel;
        label.htmlFor = useReserve ? 'maintenanceArea' : 'houseFee';
        switchButton.textContent = useReserve ? switchButton.dataset.manualLabel : switchButton.dataset.reserveLabel;
        (useReserve ? reserve.querySelector('input') : manual.querySelector('input')).focus();
    });
}

// Initialize the app once the DOM is fully loaded.
document.addEventListener('DOMContentLoaded', initApp);
